import React, { useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowRightIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import ActionButton from "../utils/ActionButton";

const UnemploymentRate = () => {
  const [showModal, setShowModal] = useState(false);

  const data = [
    {
      year: "2015",
      Jan: 3.5,
      Feb: 3.5,
      Mar: 3.5,
      Apr: 3.5,
      May: 3.5,
      Jun: 3.5,
      Jul: 3.5,
      Aug: 3.5,
      Sep: 3.5,
      Oct: 3.5,
      Nov: 3.5,
      Dec: 3.5,
    },
    {
      year: "2016",
      Jan: 3.5,
      Feb: 3.5,
      Mar: 3.5,
      Apr: 3.4,
      May: 3.4,
      Jun: 3.3,
      Jul: 3.3,
      Aug: 3.3,
      Sep: 3.2,
      Oct: 3.2,
      Nov: 3.2,
      Dec: 3.2,
    },
    {
      year: "2017",
      Jan: 3.2,
      Feb: 3.2,
      Mar: 3.2,
      Apr: 3.2,
      May: 3.2,
      Jun: 3.2,
      Jul: 3.2,
      Aug: 3.1,
      Sep: 3.1,
      Oct: 3.1,
      Nov: 3.1,
      Dec: 3.1,
    },
    {
      year: "2018",
      Jan: 3.0,
      Feb: 3.0,
      Mar: 3.0,
      Apr: 2.9,
      May: 2.9,
      Jun: 2.9,
      Jul: 2.9,
      Aug: 2.9,
      Sep: 2.9,
      Oct: 2.9,
      Nov: 2.9,
      Dec: 2.8,
    },
    {
      year: "2019",
      Jan: 2.8,
      Feb: 2.7,
      Mar: 2.7,
      Apr: 2.6,
      May: 2.6,
      Jun: 2.5,
      Jul: 2.5,
      Aug: 2.5,
      Sep: 2.4,
      Oct: 2.4,
      Nov: 2.4,
      Dec: 2.4,
    },
    {
      year: "2020",
      Jan: 2.5,
      Feb: 2.5,
      Mar: 2.5,
      Apr: 10.0,
      May: 7.7,
      Jun: 6.4,
      Jul: 5.7,
      Aug: 4.8,
      Sep: 4.5,
      Oct: 4.0,
      Nov: 3.8,
      Dec: 3.6,
    },
    {
      year: "2021",
      Jan: 3.4,
      Feb: 3.3,
      Mar: 3.1,
      Apr: 3.0,
      May: 2.9,
      Jun: 2.8,
      Jul: 2.7,
      Aug: 2.6,
      Sep: 2.5,
      Oct: 2.4,
      Nov: 2.3,
      Dec: 2.2,
    },
    {
      year: "2022",
      Jan: 2.2,
      Feb: 2.2,
      Mar: 2.3,
      Apr: 2.3,
      May: 2.3,
      Jun: 2.3,
      Jul: 2.4,
      Aug: 2.4,
      Sep: 2.5,
      Oct: 2.5,
      Nov: 2.5,
      Dec: 2.5,
    },
    {
      year: "2023",
      Jan: 2.5,
      Feb: 2.5,
      Mar: 2.5,
      Apr: 2.5,
      May: 2.6,
      Jun: 2.6,
      Jul: 2.7,
      Aug: 2.7,
      Sep: 2.8,
      Oct: 2.8,
      Nov: 2.9,
      Dec: 2.9,
    },
    {
      year: "2024",
      Jan: 2.9,
      Feb: 3.0,
      Mar: 3.0,
      Apr: 3.1,
      May: 3.2,
      Jun: 3.3,
      Jul: 3.3,
      Aug: 3.3,
      Sep: 3.3,
      Oct: 3.3,
      Nov: 3.3,
      Dec: 3.3,
    },
    {
      year: "2025",
      Jan: 3.2,
      Feb: 3.2,
      Mar: 3.1,
      Apr: 3.1,
      May: 3.2,
      Jun: 3.2,
      Jul: 3.3,
      Aug: null,
      Sep: null,
      Oct: null,
      Nov: null,
      Dec: null,
    },
  ];

  // Transform data for chart
  const monthlyTrends = Object.keys(data[0])
    .slice(1)
    .map((month) => {
      const monthData = { name: month };
      data.forEach((yearObj) => {
        monthData[yearObj.year] = yearObj[month];
      });
      return monthData;
    });

  let minValue = Infinity;
  let minYear = "";
  let minMonth = "";

  for (const yearData of data) {
    for (const [month, value] of Object.entries(yearData)) {
      // Skip the "year" property and null values
      if (month !== "year" && value !== null) {
        if (value < minValue) {
          minValue = value;
          minYear = yearData.year;
          minMonth = month;
        }
      }
    }
  }

  // Generate distinct colors for each year
  const colors = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#f97316",
    "#64748b",
    "#84cc16",
    "#06b6d4",
  ];

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white">
      {/* Landing Section */}
      <LandingSection
        data={{
          imageName: "unemployment1.webp",
          title: "Utah Unemployment Trends",
          visible: "",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Utah Unemployment Trends</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Utah’s unemployment trends show consistently low rates,
              highlighting a resilient job market and strong economic recovery
              in recent years.
            </p>

            {/* <ActionButton data="Explore More" /> */}
          </div>
        </div>
      </div>

      {/* Main Content */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-900 mb-4">
            Utah Unemployment Rate
          </h1>
          {/* <p className="text-xl text-blue-700">
                        3.2% (compared to 4.1% National) - February 2025
                    </p> */}
        </div>
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Current Rate
            </h3>
            <p className="text-3xl font-bold text-blue-600">3.3%</p>
            <p className="text-sm text-gray-500">July 2025</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              National Comparison
            </h3>
            <p className="text-3xl font-bold text-green-600">-0.9%</p>
            <p className="text-sm text-gray-500">Below national average</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Historical Low
            </h3>
            <p className="text-2xl font-bold text-blue-600">{minValue}% </p>
            <p className="text-sm text-gray-500">
              Achieved in {minMonth} {minYear}
            </p>
          </div>
        </div>

        {/* Chart Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 md:mb-0">
              Monthly Unemployment Trends (2015-2025)
            </h2>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/utah-at-a-glance/unemployment-rate/dataset"
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <DocumentTextIcon className="h-5 w-5" />
                View Dataset
              </Link>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <InformationCircleIcon className="h-5 w-5" />
                Chart Details
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="h-[500px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" />
                <YAxis
                  label={{
                    value: "Unemployment Rate (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                />
                <Tooltip
                  formatter={(value) => [`${value}%`]}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                {data.map((yearData, index) => (
                  <Line
                    key={yearData.year}
                    type="monotone"
                    dataKey={yearData.year}
                    stroke={colors[index % colors.length]}
                    strokeWidth={2}
                    activeDot={{ r: 6 }}
                    name={`Year ${yearData.year}`}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Data Sources */}
          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">Source:</span>
              <img
                src="/img/unemployment-logo.png"
                alt="U.S. Bureau of Labor Statistics"
                className="h-12 w-auto"
              />
              <span className="ml-2 text-sm text-gray-600">
                U.S. Bureau of Labor Statistics
              </span>
            </div>
            <div className="border-t border-gray-300 pt-4">
              <p className="font-semibold mb-2 text-sm text-gray-700">Additional Data Sources:</p>
              <div className="flex flex-col gap-1">
                <a
                  target="_blank"
                  href="https://jobs.utah.gov/wi/update/une/season.pdf"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Department of Workforce Services - Unemployment Update
                </a>
                <a
                  target="_blank"
                  href="https://fred.stlouisfed.org"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  FRED Economic Data
                </a>
                <a
                  target="_blank"
                  href="https://www.bls.gov/lau/"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  BLS - Local Area Unemployment Statistics
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Insights Section */}
        <div className="bg-blue-50 rounded-xl p-6 mb-12 border border-blue-200">
          <h2 className="text-2xl font-bold text-blue-900 mb-4">
            Key Insights
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Historical Trends
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-blue-500 rounded-full mt-2 mr-2"></span>
                  <span>Steady decline from 2015-2019</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-red-500 rounded-full mt-2 mr-2"></span>
                  <span>
                    Significant spike in April 2020 (10%) due to pandemic
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-green-500 rounded-full mt-2 mr-2"></span>
                  <span>Rapid recovery through 2021-2022</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">
                Recent Patterns
              </h3>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-purple-500 rounded-full mt-2 mr-2"></span>
                  <span>Slight upward trend in 2023-2025</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-yellow-500 rounded-full mt-2 mr-2"></span>
                  <span>Consistently below national average</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block h-2 w-2 bg-blue-400 rounded-full mt-2 mr-2"></span>
                  <span>Seasonal variations visible each year</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b p-4 sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold">Chart Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <h4 className="font-bold text-lg mb-3">
                Unemployment Rate Trends
              </h4>
              <p className="mb-4">
                This visualization shows monthly unemployment rate trends from
                2015 to 2025. Each colored line represents a different year,
                allowing for comparison of seasonal patterns and long-term
                trends.
              </p>

              <h5 className="font-semibold mb-2">How to Interpret:</h5>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Hover over lines to see exact unemployment rates</li>
                <li>
                  Compare year-to-year patterns by following each colored line
                </li>
                <li>
                  Note the dramatic spike in April 2020 due to COVID-19 impacts
                </li>
              </ul>

              <h5 className="font-semibold mb-2">Data Notes:</h5>
              <ul className="list-disc pl-5 space-y-2">
                <li>2025 data is preliminary and incomplete</li>
                <li>Rates are seasonally adjusted</li>
                <li>Utah consistently outperforms national averages</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UnemploymentRate;